import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs';

import { projectContextSelectors } from '@blaze-shared/app-state-data-access';
import { VariablesFacade } from '@blaze-shared/variables-data-access';

import { IntelliContextService } from '../intelli-context.service';
import { IntelliContext } from '../models/intellisense';

import * as IntelliActions from './intellisense.actions';

@Injectable()
export class IntelliContextEffects {
  private readonly service = inject(IntelliContextService);
  private readonly store = inject(Store);
  private readonly variablesFacade = inject(VariablesFacade);

  private readonly varDefs$ = toObservable(this.variablesFacade.varDefs);

  notifyIntelliContextChanged$ = createEffect(() =>
    this.service.observeContext().pipe(
      debounceTime(300),
      filter((ctx) => !!Object.keys(ctx)?.length),
      distinctUntilChanged(),
      map((ctx: IntelliContext) => {
        ctx.vars = this.variablesFacade.varDefs();
        console.log(`Editor context has been set to:`, ctx);
        return IntelliActions.contextUpdated({ ctx });
      }),
    ),
  );

  /**
   * Update value context when global vars stored in local storage change.
   */
  updateIntelliContextOnGlobalVarsChanges$ = createEffect(
    () =>
      this.store.select(projectContextSelectors.selectVarUserAndVarAccount).pipe(
        filter(({ varAccount, varUser }) => !!(varAccount || varUser)),
        map(() => {
          this.service.createContext();
        }),
      ),
    { dispatch: false },
  );

  /**
   * Update value context when variables stored on the server change.
   */
  updateIntelliContextOnStoredVarsChanges$ = createEffect(
    () =>
      this.varDefs$.pipe(
        distinctUntilChanged(),
        map(() => {
          this.service.createContext();
        }),
      ),
    { dispatch: false },
  );

  constructor() {
    this.service.createContext();
  }
}
